// src/pages/Tickets/TicketDetail.jsx
// -----------------------------------------------------------------------------
// Full detail view of a single ticket. Covers:
//   - Requirement #13: all core ticket fields
//   - Requirement #14: full status history
//   - Requirement #15/#19: comments (+ internal vs client-visible)
//   - Requirement #19/#20: assignment history / multiple assignees
//   - Attachments (Ticket_Attachment_TBL)
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ticketService from '../../api/ticketService';
import userService from '../../api/userService';
import projectService from '../../api/projectService';
import ticketStatusService from '../../api/ticketStatusService';
import ticketSeverityService from '../../api/ticketSeverityService';
import ticketTypeService from '../../api/ticketTypeService';
import projectEnvironmentService from '../../api/projectEnvironmentService';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import './tickets.css';

const TABS = ['Comments', 'History', 'Attachments', 'Assignments'];

const isImageFile = (fileName, fileType) => {
  const type = String(fileType || '').toLowerCase();
  const name = String(fileName || '').toLowerCase();
  return type.startsWith('image/') || 
         name.endsWith('.jpg') || 
         name.endsWith('.jpeg') || 
         name.endsWith('.png') || 
         name.endsWith('.gif') || 
         name.endsWith('.svg') || 
         name.endsWith('.webp');
};

const getAttachmentUrl = (filePath) => {
  if (!filePath) return '';
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath;
  const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:5020';
  const origin = apiBase.replace(/\/api\/?$/, ''); // Strip '/api' from the end
  return `${origin}${filePath}`;
};

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Comments');

  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);

  // Load the ticket + all its related tabs' data.
  useEffect(() => {
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const loadTicket = async () => {
    setLoading(true);
    try {
      const [
        ticketRes, 
        commentsRes, 
        historyRes, 
        attachmentsRes, 
        assignmentsRes, 
        usersRes,
        projectsRes,
        statusesRes,
        severitiesRes,
        typesRes,
        envsRes
      ] = await Promise.all([
        ticketService.getById(ticketId),
        ticketService.getComments(ticketId),
        ticketService.getHistory(ticketId),
        ticketService.getAttachments(ticketId),
        ticketService.getAssignments(ticketId),
        userService.getAll().catch(() => ({ data: [] })),
        projectService.getAll().catch(() => ({ data: [] })),
        ticketStatusService.getAll().catch(() => ({ data: [] })),
        ticketSeverityService.getAll().catch(() => ({ data: [] })),
        ticketTypeService.getAll().catch(() => ({ data: [] })),
        projectEnvironmentService.getAll().catch(() => ({ data: [] })),
      ]);

      const allUsers = usersRes.data?.data || usersRes.data || [];
      setUsers(allUsers);

      const rawTicket = ticketRes.data;
      const allProjects = projectsRes.data?.data || projectsRes.data || [];
      const allStatuses = statusesRes.data?.data || statusesRes.data || [];
      const allSeverities = severitiesRes.data?.data || severitiesRes.data || [];
      const allTypes = typesRes.data?.data || typesRes.data || [];
      const allEnvs = envsRes.data?.data || envsRes.data || [];

      // Find resolved names for the ticket
      const proj = allProjects.find(p => Number(p.projectID || p.projectId) === Number(rawTicket.projectID));
      const status = allStatuses.find(s => Number(s.statusID || s.statusId) === Number(rawTicket.statusID));
      const severity = allSeverities.find(s => Number(s.severityID || s.severityId) === Number(rawTicket.severityID));
      const type = allTypes.find(t => Number(t.ticketTypeID || t.ticketTypeId) === Number(rawTicket.ticketTypeID));
      const env = allEnvs.find(e => Number(e.environmentID || e.environmentId) === Number(rawTicket.environmentID));
      const creator = allUsers.find(u => Number(u.userID || u.userId) === Number(rawTicket.raisedBy));

      const resolvedTicket = {
        ...rawTicket,
        projectName: proj ? proj.projectName : (rawTicket.projectName || '-'),
        statusName: status ? status.statusName : (rawTicket.statusName || '-'),
        severityName: severity ? severity.severityName : (rawTicket.severityName || '-'),
        typeName: type ? type.typeName : (rawTicket.typeName || '-'),
        environmentName: env ? env.environmentName : (rawTicket.environmentName || 'Default'),
        raisedByName: creator ? `${creator.firstName} ${creator.lastName}` : (rawTicket.raisedByName || `User ${rawTicket.raisedBy || ''}`),
      };

      setTicket(resolvedTicket);
      
      // Resolve commenter name from users list
      const resolvedComments = (commentsRes.data || []).map(c => {
        const commenter = allUsers.find(u => Number(u.userID || u.userId) === Number(c.commentedBy));
        return {
          ...c,
          commentedByName: commenter ? `${commenter.firstName} ${commenter.lastName}` : (c.commentedByName || `User ${c.commentedBy}`),
        };
      });
      setComments(resolvedComments);

      // Resolve history changedBy name
      const resolvedHistory = (historyRes.data || []).map(h => {
        const changer = allUsers.find(u => Number(u.userID || u.userId) === Number(h.changedBy));
        const fromSt = allStatuses.find(s => Number(s.statusID || s.statusId) === Number(h.fromStatusID));
        const toSt = allStatuses.find(s => Number(s.statusID || s.statusId) === Number(h.toStatusID));
        return {
          ...h,
          changedByName: changer ? `${changer.firstName} ${changer.lastName}` : (h.changedByName || `User ${h.changedBy}`),
          fromStatusName: fromSt ? fromSt.statusName : h.fromStatusName,
          toStatusName: toSt ? toSt.statusName : h.toStatusName,
        };
      });
      setHistory(resolvedHistory);

      setAttachments(attachmentsRes.data || []);

      // Resolve assignee name from users list
      const resolvedAssignments = (assignmentsRes.data || []).map(a => {
        const assignee = allUsers.find(u => Number(u.userID || u.userId) === Number(a.userID));
        return {
          ...a,
          userName: assignee ? `${assignee.firstName} ${assignee.lastName}` : (a.userName || `User ${a.userID}`),
        };
      });
      setAssignments(resolvedAssignments);
    } catch (err) {
      toast.error('Failed to load ticket details.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      // isInternalFlag hides the comment from Client users (Requirement: internal vs client-visible comments).
      await ticketService.addComment(ticketId, {
        commentText: newComment,
        isInternalFlag: isInternalComment,
      });
      setNewComment('');
      const res = await ticketService.getComments(ticketId);
      setComments(res.data || []);
      toast.success('Comment added.');
    } catch (err) {
      toast.error('Failed to add comment.');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newSelected = files.map(file => ({
      file,
      name: file.name,
      sizeKB: Math.round(file.size / 1024),
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setSelectedFiles(prev => [...prev, ...newSelected]);
    e.target.value = '';
  };

  const handleUploadSelected = async () => {
    if (selectedFiles.length === 0) return;
    try {
      const filesToUpload = selectedFiles.map(f => f.file);
      await ticketService.uploadAttachment(ticketId, filesToUpload);
      
      selectedFiles.forEach(f => {
        if (f.url) URL.revokeObjectURL(f.url);
      });
      setSelectedFiles([]);

      const res = await ticketService.getAttachments(ticketId);
      setAttachments(res.data || []);
      toast.success('Attachments saved successfully.');
    } catch (err) {
      toast.error('Failed to save attachments.');
    }
  };

  const handleCancelSelection = () => {
    selectedFiles.forEach(f => {
      if (f.url) URL.revokeObjectURL(f.url);
    });
    setSelectedFiles([]);
  };

  const handleRemovePendingFile = (index) => {
    setSelectedFiles(prev => {
      const copy = [...prev];
      const removed = copy.splice(index, 1)[0];
      if (removed && removed.url) {
        URL.revokeObjectURL(removed.url);
      }
      return copy;
    });
  };

  const triggerDeleteConfirm = (attachmentID) => {
    setDeleteConfirmId(attachmentID);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await ticketService.removeAttachment(ticketId, deleteConfirmId);
      const res = await ticketService.getAttachments(ticketId);
      setAttachments(res.data || []);
      toast.success('Attachment deleted.');
    } catch (err) {
      toast.error('Failed to delete attachment.');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  if (loading) return <Loader label="Loading ticket..." />;
  if (!ticket) return <p>Ticket not found.</p>;

  // Client users only ever see non-internal comments on screen (an extra
  // client-side safeguard - the backend should also enforce this).
  const visibleComments = hasRole('Client') ? comments.filter((c) => !c.isInternalFlag) : comments;

  return (
    <div>
      {/* ---------- Header ---------- */}
      <div className="ticket-detail-header">
        <div>
          <h1>
            {ticket.ticketNumber} — {ticket.title}
          </h1>
          <div className="ticket-meta">
            <StatusBadge value={ticket.statusName} />
            <StatusBadge value={ticket.severityName} />
            <span className="text-muted">{ticket.typeName}</span>
          </div>
        </div>

        {/* ---------- Back Button ---------- */}
        <button 
          onClick={() => navigate('/tickets')}
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '8px', 
            fontWeight: '700',
            fontSize: '13.5px',
            padding: '10px 18px',
            borderRadius: '30px',
            backgroundColor: 'var(--color-bg-card, #ffffff)',
            color: 'var(--color-text, #1f2937)',
            border: '1px solid var(--color-border, #e5e7eb)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            outline: 'none',
            alignSelf: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
            e.currentTarget.style.borderColor = 'var(--afix-orange)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            e.currentTarget.style.borderColor = 'var(--color-border, #e5e7eb)';
          }}
        >
          <span>←</span>
          <span>Back to Tickets</span>
        </button>
      </div>

      <div className="ticket-detail-grid">
        {/* ---------- Left: description + tabs ---------- */}
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="sidebar-panel-title">Description</div>
            <p>{ticket.description}</p>
            {ticket.detailedDescription && (
              <>
                <div className="sidebar-panel-title" style={{ marginTop: 14 }}>
                  Detailed Description
                </div>
                <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.detailedDescription}</p>
              </>
            )}

            {/* Inline ticket images (Gallery) */}
            {attachments.filter(a => isImageFile(a.fileName, a.fileType)).length > 0 && (
              <div style={{ marginTop: '20px', borderTop: '1px solid var(--color-border)', paddingTop: '16px' }}>
                <div className="sidebar-panel-title" style={{ marginBottom: '12px' }}>Ticket Attachments (Images)</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                  {attachments.filter(a => isImageFile(a.fileName, a.fileType)).map((a) => (
                    <a 
                      key={a.attachmentID} 
                      href={getAttachmentUrl(a.filePath)} 
                      target="_blank" 
                      rel="noreferrer" 
                      style={{ 
                        borderRadius: 'var(--radius-sm)', 
                        overflow: 'hidden', 
                        border: '1px solid var(--color-border)',
                        display: 'block' 
                      }}
                      title={a.fileName}
                    >
                      <img 
                        src={getAttachmentUrl(a.filePath)} 
                        alt={a.fileName} 
                        style={{ 
                          width: '100%', 
                          height: '90px', 
                          objectFit: 'cover', 
                          display: 'block' 
                        }} 
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="tabs">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* ---------- Comments tab ---------- */}
            {activeTab === 'Comments' && (
              <div>
                <form onSubmit={handleAddComment} style={{ marginBottom: 16 }}>
                  <div className="form-group">
                    <textarea
                      rows={3}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Team-only comments are only offered to internal roles, not Clients */}
                    {!hasRole('Client') && (
                      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                        />
                        Internal (team-only) comment
                      </label>
                    )}
                    <button type="submit" className="btn btn-primary">
                      Post Comment
                    </button>
                  </div>
                </form>

                {visibleComments.length === 0 ? (
                  <p className="text-muted">No comments yet.</p>
                ) : (
                  visibleComments.map((c) => (
                    <div className="comment-item" key={c.commentID}>
                      <div className="comment-meta">
                        <span>
                          {c.commentedByName} {c.isInternalFlag && <span className="badge badge-warning">Internal</span>}
                        </span>
                        <span>{new Date(c.createdDttm).toLocaleString()}</span>
                      </div>
                      <div className="comment-text">{c.commentText}</div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ---------- History tab (Requirement #14) ---------- */}
            {activeTab === 'History' && (
              <div>
                {history.length === 0 ? (
                  <p className="text-muted">No status history yet.</p>
                ) : (
                  history.map((h) => (
                    <div className="timeline-item" key={h.historyID}>
                      <span className="timeline-dot" />
                      <div>
                        <strong>{h.fromStatusName || 'Created'}</strong> → <strong>{h.toStatusName}</strong>
                        <div className="text-muted">
                          by {h.changedByName} on {new Date(h.changedDttm).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ---------- Attachments tab ---------- */}
            {activeTab === 'Attachments' && (
              <div>
                <div style={{ marginBottom: 20 }}>
                  <input 
                    type="file" 
                    id="attachment-file-input" 
                    onChange={handleFileSelect} 
                    style={{ display: 'none' }} 
                    multiple
                  />
                  <label 
                    htmlFor="attachment-file-input" 
                    className="btn btn-primary" 
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    📎 Select Files to Add
                  </label>
                </div>

                {/* Pending Staged Files Section */}
                {selectedFiles.length > 0 && (
                  <div style={{ 
                    background: 'rgba(255, 137, 6, 0.05)', 
                    border: '1.5px dashed var(--afix-orange)', 
                    borderRadius: 'var(--radius-md)', 
                    padding: '16px', 
                    marginBottom: '24px' 
                  }}>
                    <h3 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '12px', color: 'var(--afix-orange-dark)' }}>
                      Pending Selection ({selectedFiles.length})
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                      {selectedFiles.map((f, idx) => (
                        <div 
                          key={idx} 
                          className="card" 
                          style={{ 
                            padding: '6px', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: '6px',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-sm)',
                            position: 'relative',
                            background: 'var(--color-bg-card)'
                          }}
                        >
                          {/* Remove from selection button */}
                          <button
                            onClick={() => handleRemovePendingFile(idx)}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '10px',
                              zIndex: 15
                            }}
                            title="Remove from selection"
                          >
                            ✕
                          </button>
                          {f.url ? (
                            <img 
                              src={f.url} 
                              alt={f.name} 
                              style={{ 
                                width: '100%', 
                                height: '80px', 
                                objectFit: 'cover', 
                                borderRadius: 'var(--radius-sm)' 
                              }} 
                            />
                          ) : (
                            <div style={{ height: '80px', display: 'flex', alignItems: 'center', fontSize: '32px' }}>📄</div>
                          )}
                          <div style={{ fontSize: '11px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%', textAlign: 'center', fontWeight: '600' }} title={f.name}>
                            {f.name}
                          </div>
                          <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>{f.sizeKB} KB</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button 
                        onClick={handleUploadSelected} 
                        className="btn btn-primary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: '700' }}
                      >
                        💾 Save Attachments
                      </button>
                      <button 
                        onClick={handleCancelSelection} 
                        className="btn btn-secondary"
                        style={{ fontWeight: '700' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {attachments.length === 0 ? (
                  <p className="text-muted">No attachments yet.</p>
                ) : (
                  <>
                    {/* Render Image Gallery */}
                    {attachments.some(a => isImageFile(a.fileName, a.fileType)) && (
                      <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: 'var(--color-text)' }}>
                          Image Gallery
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
                          {attachments.filter(a => isImageFile(a.fileName, a.fileType)).map((a) => (
                            <div 
                              key={a.attachmentID} 
                              className="card" 
                              style={{ 
                                padding: '8px', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                gap: '8px',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                overflow: 'hidden',
                                position: 'relative'
                              }}
                            >
                              {/* Delete button overlay */}
                              <button
                                onClick={() => triggerDeleteConfirm(a.attachmentID)}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  background: 'rgba(239, 68, 68, 0.95)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '26px',
                                  height: '26px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                  zIndex: 10,
                                  fontWeight: 'bold',
                                  fontSize: '11px',
                                  transition: 'background 0.2s'
                                }}
                                title="Delete Attachment"
                              >
                                🗑️
                              </button>

                              <a href={getAttachmentUrl(a.filePath)} target="_blank" rel="noreferrer" style={{ width: '100%' }}>
                                <img 
                                  src={getAttachmentUrl(a.filePath)} 
                                  alt={a.fileName} 
                                  style={{ 
                                    width: '100%', 
                                    height: '140px', 
                                    objectFit: 'cover', 
                                    borderRadius: 'var(--radius-sm)',
                                    display: 'block'
                                  }} 
                                />
                              </a>
                              <div style={{ width: '100%', fontSize: '12px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: '600' }} title={a.fileName}>
                                {a.fileName}
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                                {a.fileSizeKB} KB
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Render All/Other Files List */}
                    <div>
                      <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: 'var(--color-text)' }}>
                        All Files ({attachments.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {attachments.map((a) => {
                          const isImg = isImageFile(a.fileName, a.fileType);
                          return (
                            <div 
                              key={a.attachmentID} 
                              className="comment-item" 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                background: 'var(--color-bg-card)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '20px' }}>{isImg ? '🖼️' : '📄'}</span>
                                <div>
                                  <a href={getAttachmentUrl(a.filePath)} target="_blank" rel="noreferrer" style={{ fontWeight: '600', color: 'var(--afix-orange)', textDecoration: 'none' }}>
                                    {a.fileName}
                                  </a>
                                  <div className="text-muted" style={{ fontSize: '12px' }}>
                                    {a.fileSizeKB} KB • Virus Scan: <strong style={{ color: a.virusScanStatus === 'Clean' ? '#22c55e' : '#f59e0b' }}>{a.virusScanStatus}</strong>
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <a 
                                  href={getAttachmentUrl(a.filePath)} 
                                  download={a.fileName}
                                  className="btn btn-secondary btn-sm"
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ padding: '6px 12px', fontSize: '12px' }}
                                >
                                  ⬇️ Download
                                </a>
                                <button 
                                  onClick={() => triggerDeleteConfirm(a.attachmentID)}
                                  className="btn btn-danger btn-sm"
                                  style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: '#ef4444', color: 'white' }}
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ---------- Assignments tab (Requirement #19, #20) ---------- */}
            {activeTab === 'Assignments' && (
              <div>
                {assignments.length === 0 ? (
                  <p className="text-muted">Not yet assigned.</p>
                ) : (
                  assignments.map((a) => (
                    <div key={a.assignmentID} className="comment-item">
                      <strong>{a.userName}</strong>
                      {a.isCurrentFlag && <span className="badge badge-success" style={{ marginLeft: 8 }}>Current</span>}
                      <div className="text-muted">Assigned {new Date(a.assignedDttm).toLocaleString()}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* ---------- Right: ticket meta sidebar ---------- */}
        <div className="card" style={{ alignSelf: 'start' }}>
          <div className="sidebar-panel-title">Details</div>
          <p><strong>Project:</strong> {ticket.projectName}</p>
          <p><strong>Environment:</strong> {ticket.environmentName || 'Default'}</p>
          <p><strong>Raised By:</strong> {ticket.raisedByName}</p>
          <p><strong>Created:</strong> {new Date(ticket.createDate).toLocaleDateString()}</p>
          {ticket.resolvedDate && (
            <p><strong>Resolved:</strong> {new Date(ticket.resolvedDate).toLocaleDateString()}</p>
          )}
          {ticket.reopenCount > 0 && <p><strong>Reopened:</strong> {ticket.reopenCount} time(s)</p>}
        </div>
      </div>

      {/* Custom Deletion Confirmation Modal */}
      {deleteConfirmId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'var(--color-bg-card, #ffffff)',
            borderRadius: 'var(--radius-md, 12px)',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid var(--color-border)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '800', color: 'var(--color-text)' }}>
              Confirm Deletion
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14.5px', color: 'var(--color-text-muted)', lineHeight: '1.5' }}>
              Are you sure you want to delete this attachment? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                onClick={() => setDeleteConfirmId(null)}
                className="btn btn-secondary"
                style={{ fontWeight: '700' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmDelete}
                className="btn btn-danger"
                style={{ 
                  background: '#ef4444', 
                  color: 'white', 
                  border: 'none', 
                  fontWeight: '700',
                  padding: '8px 16px',
                  borderRadius: 'var(--radius-sm)'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}